import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppDatePicker from "../../components/ui/AppDatePicker";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import ImageUploadDragAndDrop from "../../components/ui/ImageUploadDragAndDrop";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { processFile } from "../../lib/utils";
import { Driver } from "./@types";
import {
  useAddDriverMutation,
  useUpdateDriverMutation,
} from "./driverApiSlice";

export const addDriverSchema = z.object({
  fullName: z.string().nonempty("Full name is required"),
  email: z.string().nonempty("Please enter a valid email address"),
  phoneNumber: z.string().nonempty("Phone number is required"),
  address: z.string().nonempty("Address is required"),
  licenseDocument: z
    .any()
    .refine(
      (val) =>
        val !== null &&
        val !== "" &&
        (typeof val === "string" || val instanceof File),
      { message: "Document is required" }
    ),

  licenseExpiryDate: z.string().nonempty("License expiry date is required"),
});

export type AddDriverForm = z.infer<typeof addDriverSchema>;

interface DriverModelProps {
  isOpen: boolean;
  onClose: () => void;
  driver?: Driver | null;
  mode?: "add" | "edit";
}

const DriverModel: React.FC<DriverModelProps> = ({
  isOpen,
  onClose,
  driver = null,
  mode = driver ? "edit" : "add",
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const toast = useToast();
  const isEditMode = mode === "edit" && driver;

  const [
    addDriver,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddDriverMutation();

  const [
    updateDriver,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateDriverMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  // Helper function to get default expiry date
  const getDefaultExpiryDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    setValue,
    reset,
    clearErrors,
    trigger,
  } = useForm<AddDriverForm>({
    resolver: zodResolver(addDriverSchema),
    mode: "onTouched",
    shouldUnregister: false,
    defaultValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      address: "",
      licenseDocument: null,
      licenseExpiryDate: getDefaultExpiryDate(),
    },
  });

  useEffect(() => {
    if (isEditMode && driver) {
      reset({
        fullName: driver.fullName || "",
        email: driver.email || "",
        phoneNumber: driver.phoneNumber || "",
        address: driver.address || "",
        licenseDocument: driver.licenseUrl || "",
        licenseExpiryDate: driver.licenseExpiryDate || getDefaultExpiryDate(),
      });
    } else if (!isEditMode) {
      reset({
        fullName: "",
        email: "",
        phoneNumber: "",
        address: "",
        licenseDocument: null,
        licenseExpiryDate: getDefaultExpiryDate(),
      });
    }
  }, [isEditMode, driver, reset]);

  // Reset step when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const handleFileUpload = (name: keyof AddDriverForm, file: File | null) => {
    // Clean up previous preview URL to prevent memory leaks
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    setValue(name, file, { shouldValidate: true, shouldDirty: true });

    if (file) {
      // Create preview URL for the new file
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
      clearErrors(name);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleNextStep = async () => {
    // Validate step 1 fields
    const step1Fields = [
      "fullName",
      "email",
      "phoneNumber",
      "address",
    ] as const;
    const isStep1Valid = await trigger(step1Fields);

    if (isStep1Valid) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    // When going back to step 1, don't reset the form values
    setCurrentStep(1);
  };

  const onSubmit = async (data: AddDriverForm) => {
    if (isSubmitting || isLoading) return;

    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const imageData = await processFile(data.licenseDocument, "image");

      const payload = {
        fullName: data.fullName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        licenseExpiryDate: data.licenseExpiryDate,
        address: data.address,
        licenseDocument: imageData,
      };

      if (isEditMode && driver?.id) {
        await updateDriver({
          id: driver.id,
          ...payload,
        }).unwrap();
      } else {
        await addDriver(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} driver. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Driver ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} driver. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setShowSuccess(false);
    setCurrentStep(1);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-2 p-6">
      {/* Step 1 */}
      <div
        className={`h-2 flex-1 rounded-full transition-colors ${
          currentStep >= 1 ? "bg-[#BDCC87]" : "bg-gray-200 text-gray-600"
        }`}
      />

      {/* Step 2 */}
      <div
        className={`h-2 flex-1 rounded-full transition-colors ${
          currentStep >= 2 ? "bg-[#BDCC87]" : "bg-gray-200 text-gray-600"
        }`}
      />
    </div>
  );

  const renderStep1 = () => (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <Controller
          control={control}
          name="fullName"
          render={({ field }) => (
            <AppInputField
              label="Full Name"
              type="text"
              error={errors.fullName?.message}
              {...field}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter full name"
            />
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <AppInputField
              label="Email Address"
              type="email"
              error={errors.email?.message}
              {...field}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter email address"
            />
          )}
        />

        <Controller
          control={control}
          name="phoneNumber"
          render={({ field }) => (
            <AppInputField
              label="Phone Number"
              type="tel"
              error={errors.phoneNumber?.message}
              {...field}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter phone number"
            />
          )}
        />

        <Controller
          control={control}
          name="address"
          render={({ field }) => (
            <AppInputField
              label="Address"
              type="text"
              error={errors.address?.message}
              {...field}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter address"
            />
          )}
        />
      </div>

      {/* Step 1 Navigation */}
      <div className="flex justify-end items-center space-x-4 pt-4">
        <AppButton
          type="button"
          onClick={handleCloseModal}
          className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px]"
        >
          Cancel
        </AppButton>

        <AppButton
          type="button"
          onClick={handleNextStep}
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px]"
        >
          Continue
        </AppButton>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="p-6 space-y-6">
      <div className="space-y-6">
        {/* File Upload */}
        <div>
          <Controller
            control={control}
            name="licenseDocument"
            render={({ field }) => (
              <Controller
                name="licenseDocument"
                control={control}
                render={({ field }) => (
                  <ImageUploadDragAndDrop
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          />
          {errors.licenseDocument && touchedFields.licenseDocument && (
            <p className="text-red-500 text-sm mt-1 flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.licenseDocument.message?.toString()}
            </p>
          )}
        </div>

        {/* Expiry Date */}
        <Controller
          control={control}
          name="licenseExpiryDate"
          render={({ field }) => {
            // Safely parse the date value
            let dateValue: Date | null = null;

            if (field.value) {
              try {
                dateValue = new Date(field.value);
                // Check if the date is valid
                if (isNaN(dateValue.getTime())) {
                  dateValue = null;
                }
              } catch {
                dateValue = null;
              }
            }

            return (
              <AppDatePicker
                label="Document Expiry Date"
                error={
                  touchedFields.licenseExpiryDate
                    ? errors.licenseExpiryDate?.message
                    : undefined
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                value={dateValue}
                onChange={(date) => {
                  if (date) {
                    field.onChange(date.toLocaleDateString("en-CA"));
                  } else {
                    field.onChange("");
                  }
                }}
                minDate={new Date()}
              />
            );
          }}
        />
      </div>

      {/* Step 2 Navigation */}
      <div className="flex justify-center items-center space-x-4 pt-4">
        <AppButton
          type="button"
          onClick={handlePrevStep}
          className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px]"
        >
          Skip for now
        </AppButton>

        <AppButton
          type="submit"
          disabled={isSubmitting || isLoading}
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 text-[15px]"
        >
          {isSubmitting || isLoading ? (
            <Spinner />
          ) : isEditMode ? (
            "Update driver"
          ) : (
            "Continue"
          )}
        </AppButton>
      </div>
    </div>
  );

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Driver has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Upload"} document`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white px-2 pb-7 overflow-hidden"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          {renderStepIndicator()}
          <div hidden={currentStep !== 1}>{renderStep1()}</div>
          <div hidden={currentStep !== 2}>{renderStep2()}</div>
        </form>
      )}
    </Modal>
  );
};

export default DriverModel;
